import { useEffect, useRef, useState } from "react";
import { TenantModel } from "../../model/data/tenant";
import {
  createTenant,
  getAllTenant,
  updateTenant,
  softDeleteTenant
} from "../../service/tenant/TenantService";
import { CreateTenantProps, GetAllTenantProps } from "../../dtos/tenant";
import { useDispatch, useSelector } from "react-redux";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { PaginationModel } from "../../model/common/paginationModel";
import { CreateTenant } from "./createTenant";
import { Toolbar } from "primereact/toolbar";
import { Toast } from "primereact/toast";
import { Button } from "../ui/Button";
import { Card } from "../ui/Card";
import { Input } from "../ui/Input";
import { StatusBadge } from "../ui/StatusBadge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faPencil, faSearch, faTrash } from "@fortawesome/free-solid-svg-icons";
import { PermissionVariables } from "../../shared/constant/PermissionVariables";
import { findSettingsByKey } from "../../service/common/commonService";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";

export const Tenant = () => {
  const [data, setData] = useState<TenantModel[]>([]);
  const [count, setCount] = useState<number>(0);
  const dispatch = useDispatch();
  const [paginationModel, setPaginationModel] = useState<PaginationModel>(
    new PaginationModel()
  );
  const [open, setOpen] = useState<boolean>(false);
  const [selectedTenant, setSelectedTenant] = useState<TenantModel | null>(
    null
  );
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>("");
  const toast = useRef<Toast>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const userPermissions = useSelector(
    (state: any) => state.permission.permissions
  );
  const localization = useSelector((state: any) => state.localization);

  const getAllData = async () => {
    const payload: GetAllTenantProps = {
      Limit: paginationModel.rows,
      Page: paginationModel.page,
      SortBy: paginationModel.sortOrder === 1 ? "ASC" : "DESC",
      SortProperty: paginationModel.sortField,
      Filter: searchText,
    };

    getAllTenant(payload, dispatch)
      .then((res) => {
        setData(res?.List);
        setCount(res?.TotalCount);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const handleOpen = () => {
    setOpen(true);
    setSelectedTenant(new TenantModel());
  };

  const handleEditOpen = async (tenant: null | TenantModel) => {
    setSelectedTenant(tenant);
    setOpen(true);
    setIsEdit(true);
  };

  const handleClose = () => {
    setOpen(false);
    setIsEdit(false);
  };

  const onSort = (event: any) => {
    setPaginationModel((prev: PaginationModel) => {
      const newState: PaginationModel = {
        ...prev,
        sortOrder: event.sortOrder,
        sortField: event.sortField,
      };
      return newState;
    });
  };

  const onPageChange = (event: any) => {
    setPaginationModel(event);
  };

  const showToast = (severity: any, summary: string, detail: string) => {
    toast && toast?.current?.show({ severity, summary, detail });
  };

  const handleSubmit = (data: TenantModel) => {
    const payload: CreateTenantProps = {
      name: data.name,
      tenancyName: data.tenancyName,
      isActive: data.isActive,
      connectionString: data?.connectionString,
      defaultUser: data.defaultUser,
      residentStatus: data.residentStatus
    };

    if (isEdit) {
      updateTenant(data?.id, payload, dispatch)
        .then((res) => {
          getAllData();
          handleClose();
          showToast('success', localization?.Success || 'Success', res);
        })
        .catch(err => {
          console.error(err);
          showToast('error', localization?.Error || 'Error', err.message);
        });
      return;
    }

    setIsLoading(true);
    createTenant(payload, dispatch, (msg) => {
      const type = msg.status === 'error' ? 'error'
        : msg.status === 'success' ? 'success' : 'info';
      showToast(type, msg.status, msg.message);

      if (msg.status === 'completed') {
        handleClose();
        setIsLoading(false);
        getAllData();
      }
    }).catch(err => {
      setIsLoading(false);
      showToast('error', localization?.Error || 'Error', err.message);
    });
  };

  const handleDelete = async (id: number) => {
    try {
      await softDeleteTenant(id, dispatch);
      showToast('success', localization?.Success || 'Success', localization?.TenantDeleted || 'Tenant deleted successfully');
      getAllData();
    } catch (err: any) {
      console.error(err);
      showToast('error', localization?.Error || 'Error', err.message);
    }
  };

  const confirmDelete = (rowData: any) => {
    confirmDialog({
      message: (
        <div className="text-center p-4">
          <p className="mb-5 text-base font-medium">
            Do you really want to delete this <strong>"{rowData.name}"</strong> tenant?
          </p>
        </div>
      ),
      header: "Delete Confirmation",
      className: "del_modal",
      acceptClassName: "p-button-danger",
      accept: () => handleDelete(rowData.id),
      // reject: () => showToast('warn', 'Rejected', 'You have rejected'),
    });
  };

  // const StatusTemplate = (rowData: any) => {
  //   return (
  //     <StatusBadge
  //       status={rowData.isActive ? 'active' : 'draft'}
  //     />
  //   );
  // };
  const StatusTemplate = (rowData: any) => {
    // Show deleted status if tenant is soft deleted
    if (rowData.deletedAt) {
      return <StatusBadge status="deleted" />; // You'll need to add 'deleted' style in StatusBadge
    }
    return (
      <StatusBadge
        status={rowData.isActive ? 'active' : 'draft'}
      />
    );
  };

  const EditTemp = (rowData: any) => {
    return (
      <Button
        className="bg-gold text-white hover:bg-charcoal py-1 px-2 text-xs"
        onClick={() => handleEditOpen(rowData)}
      >
        <FontAwesomeIcon icon={faPencil} />{" "}
        {localization?.Edit || "Edit"}
      </Button>
    );
  };

  const startToolbar = () => {
    return (
      <div>
        <h4 className="text-xl font-bold surface-900 mb-4 mt-3">{localization?.Tenants || "Tenants"}</h4>
      </div>
    );
  };

  const endToolbar = () => {
    return (
      <div className="flex items-center gap-4">
        <div className="relative">
          {/* <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
            <FontAwesomeIcon icon={faSearch} />
          </span> */}
          <Input
            className="pl-10"
            type="search"
            placeholder={localization?.SearchTenants || "Search tenants..."}
            onChange={(e) => {
              setTimeout(() => {
                setSearchText(e.target.value.trim());
              }, 500);
            }}
          />
        </div>
        {userPermissions?.includes(PermissionVariables.createTenant) && <Button
          variant="primary"
          className="h-[40px]"
          size="md"
          onClick={handleOpen}
        >
          {localization?.CreateTenant || "Create Tenant"}
        </Button>}
      </div>
    );
  };

  useEffect(() => {
    getAllData();
  }, [paginationModel, searchText]);

  const ActionTemp = (rowData: any) => {
    return (
      <div className="flex space-x-2">
        {userPermissions?.includes(PermissionVariables.editTenant) && (
          <Button
            variant="secondary"
            className="actionBtn h-8 w-8 p-0 flex items-center bg-transparent hover:bg-gold justify-center border-gold text-gold hover:text-white transition-all"
            onClick={() => handleEditOpen(rowData)}
          >
            <FontAwesomeIcon icon={faPen} className="text-sm" />
          </Button>
        )}
        {userPermissions?.includes(PermissionVariables.deleteUser) && (
          <Button
            variant="danger"
            className="actionBtn h-8 w-8 p-0 flex items-center justify-center bg-transparent hover:bg-red-500 border-alert-red text-alert-red hover:text-white transition-all"
            onClick={() => confirmDelete(rowData)}
          >
            <FontAwesomeIcon icon={faTrash} className="text-sm" />
          </Button>
        )}
      </div>
    );
  };
  return (
    <div className="block">
      <Toast ref={toast} />
      <ConfirmDialog />
      <Toolbar
        start={startToolbar}
        end={endToolbar}
        className="mb-6 p-0 rounded-md bg-transparent border-none"
      />

      <Card className="mb-6 card_content_padding">
        <DataTable
          value={data}
          dataKey="id"
          lazy
          paginator
          first={paginationModel.first}
          rows={paginationModel.rows}
          totalRecords={count}
          onSort={onSort}
          onPage={onPageChange}
          sortOrder={paginationModel.sortOrder}
          sortField={paginationModel.sortField}
          rowHover
          stripedRows
          emptyMessage={localization?.NoTenantsFound || "No tenants found"}
          className="p-datatable-sm"
          currentPageReportTemplate={localization?.TotalRecords || "Total Records {totalRecords}"}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        >
          <Column
            field="name"
            header={localization?.Name || "Name"}
            sortable
            className="font-medium"
          />
          <Column
            field="tenancyName"
            header={localization?.TenancyName || "Tenancy Name"}
            sortable
          />
          <Column
            field="isActive"
            header={localization?.Status || "Status"}
            body={StatusTemplate}
            className="text-left"
          />
          {userPermissions?.includes(PermissionVariables.editTenant) && <Column
            field="id"
            header={localization?.Action || "Action"}
            body={ActionTemp}
            className="text-left"
            style={{ width: '12rem' }}
          />}
        </DataTable>
      </Card>

      <CreateTenant
        open={open}
        isEdit={isEdit}
        isLoading={isLoading}
        handleClose={handleClose}
        data={selectedTenant}
        setData={setSelectedTenant}
        handleSubmit={handleSubmit}
      />
    </div>
  );
};